import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

export default function Visualization({ results, scaleResults, dmus, inspectedDMU, orientation = 'input' }) {
  
  // 1. DATA: Efficiency Scores
  const effData = (results || []).map(res => ({ 
    dmu: res.dmu, 
    efficiency: res.efficiency ? parseFloat(res.efficiency) : 0 
  }));

  // 2. DATA: Peer Frequency (Who is the most popular benchmark?)
  const peerCounts = {};
  (results || []).forEach(res => {
    if (res.peers) {
      res.peers.forEach(p => {
        peerCounts[p.name] = (peerCounts[p.name] || 0) + 1;
      });
    }
  });
  
  const peerData = Object.keys(peerCounts)
    .map(name => ({ name, count: peerCounts[name] }))
    .sort((a, b) => b.count - a.count); // Sort highest first

  // 3. DATA: Targets for Selected DMU (Actual vs Target)
  let targetData = [];
  if (inspectedDMU && inspectedDMU.targets) {
    // Combine Inputs
    inspectedDMU.targets.inputs.forEach((t, i) => {
      // Find original value
      const original = dmus.find(d => d.name === inspectedDMU.dmu).inputs[i];
      targetData.push({
        variable: `Input ${i+1}`,
        Type: 'Input',
        Actual: original,
        Target: parseFloat(t),
        Reduction: (original - parseFloat(t)).toFixed(2)
      });
    });
    // Combine Outputs
    inspectedDMU.targets.outputs.forEach((t, i) => {
      const original = dmus.find(d => d.name === inspectedDMU.dmu).outputs[i];
      targetData.push({
        variable: `Output ${i+1}`,
        Type: 'Output',
        Actual: original,
        Target: parseFloat(t),
        Increase: (parseFloat(t) - original).toFixed(2)
      });
    });
  }

  // 4. DATA: Scale Efficiency
  const scaleData = (scaleResults || []).map(res => ({
    dmu: res.dmu,
    ccr: parseFloat(res.ccrEff),
    bcc: parseFloat(res.bccEff),
    se: parseFloat(res.scaleEff),
  }));

  return (
    <div style={{ marginTop: '20px' }}>
      
      {/* --- DRILL DOWN SECTION (Only shows if a row is clicked) --- */}
      {inspectedDMU && (
        <div style={{ background: '#e3f2fd', padding: '20px', borderRadius: '8px', marginBottom: '40px', border: '1px solid #90caf9' }}>
          <h3 style={{ marginTop: 0, color: '#0d47a1' }}>Analysis for: {inspectedDMU.dmu}</h3>
          <p><strong>Efficiency:</strong> {inspectedDMU.solverFailed ? '—' : inspectedDMU.efficiency} | <strong>Status:</strong> {inspectedDMU.solverFailed ? 'Not solved' : inspectedDMU.isEfficient ? 'Efficient' : 'Inefficient'}</p>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {/* Peers List */}
            <div style={{ flex: 1, minWidth: '300px' }}>
              <h4>Benchmarks (Peers)</h4>
              {inspectedDMU.peers.length > 0 ? (
                <ul style={{ paddingLeft: '20px' }}>
                  {inspectedDMU.peers.map((p, idx) => (
                    <li key={idx}>
                      <strong>{p.name}</strong> (Weight: {p.lambda.toFixed(2)})
                    </li>
                  ))}
                </ul>
              ) : (
                <p>
                  {inspectedDMU.solverFailed
                    ? 'Peers are unavailable because this unit could not be solved.'
                    : 'This unit is a benchmark for itself.'}
                </p>
              )}
            </div>

            {/* Improvement Chart */}
            <div style={{ flex: 2, minWidth: '300px', height: '300px' }}>
              <h4>Improvement Targets (Actual vs Target)</h4>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={targetData} layout="vertical" margin={{ left: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="variable" type="category" width={80} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Actual" fill="#8884d8" name="Current Value" />
                  <Bar dataKey="Target" fill="#82ca9d" name="Target Value" />
                </BarChart>
              </ResponsiveContainer>
              <small>
                {orientation === 'input'
                  ? 'Input targets should be no greater than current inputs; output targets should be no less than current outputs.'
                  : 'Output targets should be no less than current outputs; input targets should be no greater than current inputs.'}
              </small>
            </div>
          </div>
        </div>
      )}

      {/* --- GLOBAL CHARTS --- */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
        
        {/* 1. Efficiency Chart */}
        {effData.length > 0 && (
          <div style={{ flex: 1, minWidth: '400px', height: '350px' }}>
            <h4>Efficiency Scores</h4>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={effData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dmu" />
                <YAxis domain={[0, 1]} />
                <Tooltip />
                <Bar dataKey="efficiency" fill="#8884d8">
                  {effData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.efficiency >= 0.999 ? '#82ca9d' : '#8884d8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 2. Peer Frequency Chart */}
        {peerData.length > 0 && (
          <div style={{ flex: 1, minWidth: '400px', height: '350px' }}>
            <h4>Benchmark Frequency (Peer Count)</h4>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={peerData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} label={{ value: 'Times Used', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Bar dataKey="count" fill="#ff7300" name="Times used as Peer" />
              </BarChart>
            </ResponsiveContainer>
            <p style={{fontSize: '0.8rem', textAlign: 'center'}}>Which efficient units act as role models most often?</p>
          </div>
        )}
      </div>

      {/* 3. Scale Chart */}
      {scaleData.length > 0 && (
        <div style={{ marginTop: '40px', height: '350px' }}>
          <h4>Scale Efficiency Comparison</h4>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={scaleData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dmu" />
              <YAxis domain={[0, 1]} />
              <Tooltip />
              <Legend />
              <Bar dataKey="ccr" fill="#8884d8" name="CCR (Total)" />
              <Bar dataKey="bcc" fill="#82ca9d" name="BCC (Technical)" />
              <Bar dataKey="se" fill="#ffc658" name="Scale Eff" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}